import { refreshAllStuckProcessingStatuses } from './processingStatus'
import { syncAll } from './orchestrator'
import { purgeExpiredAudioBlobs } from '../db'

// The on-device audio retention window is measured in days, so the reclaim sweep
// only needs to re-check occasionally; throttle it to at most hourly.
const AUDIO_SWEEP_INTERVAL_MS = 60 * 60 * 1000

/**
 * Setup automatic background sync when online
 */
export function setupBackgroundSync(options?: { canSync?: () => boolean }): () => void {
  if (typeof window === 'undefined') {
    return () => {}
  }

  const canSync = options?.canSync ?? (() => true)
  const runSyncWithLogging = async (): Promise<void> => {
    if (!canSync()) {
      return
    }
    const result = await syncAll()
    if (result.synced > 0) {
      console.log(`Synced ${result.synced} items`)
    }
    if (result.failed > 0) {
      console.warn(`Failed to sync ${result.failed} items`)
    }
  }

  // Reclaim local audio whose retention window has elapsed. Local-only (no
  // network needed), so it runs regardless of connectivity. Throttled so a
  // long-lived session re-checks at most hourly.
  let lastAudioSweepAt = 0
  const runAudioSweep = (): void => {
    lastAudioSweepAt = Date.now()
    void purgeExpiredAudioBlobs()
  }

  // Kick off an initial sync if we're already online
  if (navigator.onLine) {
    void runSyncWithLogging()
  }
  // Sweep at launch to reclaim items that aged out while the app was closed.
  runAudioSweep()

  // Sync when coming online
  const handleOnline = async (): Promise<void> => {
    console.log('Connection restored, syncing...')
    await runSyncWithLogging()
  }
  window.addEventListener('online', handleOnline)

  // Periodic sync while online.
  const interval = window.setInterval(() => {
    if (navigator.onLine) {
      void runSyncWithLogging()
      void refreshAllStuckProcessingStatuses()
    }
    if (Date.now() - lastAudioSweepAt >= AUDIO_SWEEP_INTERVAL_MS) {
      runAudioSweep()
    }
  }, 20 * 1000) // 20 seconds while online

  return () => {
    window.removeEventListener('online', handleOnline)
    clearInterval(interval)
  }
}
